// RadicalAdvancedMode.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
    Icons,
    PICTOGRAM_LAYOUT_PRESETS,
    createUserPresetId,
    getCardTextColor,
    getLetters,
    getPictogramsForPackets,
    normalizeCardText,
} from "./RadicalHelpers";
import {
    RadicalConfirmDialog,
    RadicalRenamePresetDialog,
    RadicalSavePresetDialog,
    RadicalUnsavedChangesDialog,
} from "./RadicalPresetDialogs";
import RadicalPictogramSelector from "./RadicalPictogramSelector";

const MAX_POSITIONS = 128;

const PACKET_NAME_RESET_FIELDS = new Set([
    "packets",
    "collectOrder",
    "separate",
    "edge",
    "points",
    "complete",
    "start",
    "direction",
]);

const copyPacketNames = (packetNames, count) =>
    Array.from({ length: count }, (_, index) =>
        String(packetNames?.[index] || ""),
    );

const applyPacketNames = (packetNames, overrides, count) =>
    Array.from({ length: count }, (_, index) =>
        String(overrides?.[index] || packetNames?.[index] || ""),
    );

const getGroupedPacketNames = (groups, packetNames) =>
    groups.map((group) =>
        group.length === 1 ? String(packetNames?.[group[0]] || "") : "",
    );

const createRandomSeed = () =>
    Math.floor(Math.random() * 0xffffffff) || Date.now();

const shufflePacket = (packet, seed) => {
    const shuffled = [...packet];
    let value = Number(seed) >>> 0;

    const random = () => {
        value += 0x6d2b79f5;

        let result = value;

        result = Math.imul(result ^ (result >>> 15), result | 1);
        result ^= result + Math.imul(result ^ (result >>> 7), result | 61);

        return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
    };

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
        const target = Math.floor(random() * (index + 1));

        [shuffled[index], shuffled[target]] = [
            shuffled[target],
            shuffled[index],
        ];
    }

    return shuffled;
};

const createId = () =>
    `step-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const getAdvancedRecipeData = (
    description,
    sequence,
    values,
    deckOrientation,
    pictogramsByPackets,
) => ({
    description,
    data: {
        sequence: sequence.map(({ id, ...step }) => step),
        values: values.map((value) => String(value ?? "")),
        deckOrientation,
        pictogramsByPackets,
    },
});

const clampInt = (value, fallback = 1, min = 1, max = 64) => {
    const parsed = parseInt(value, 10);

    return Number.isNaN(parsed)
        ? fallback
        : Math.min(max, Math.max(min, parsed));
};

const parseNumberList = (value) =>
    String(value || "")
        .split(/[\s,;|/]+/)
        .map((item) => parseInt(item, 10))
        .filter((item) => !Number.isNaN(item));

const normalizeOrder = (value, count) => {
    const used = [];

    parseNumberList(value).forEach((number) => {
        const index = number - 1;

        if (index >= 0 && index < count && !used.includes(index)) {
            used.push(index);
        }
    });

    return [
        ...used,
        ...Array.from({ length: count }, (_, index) => index).filter(
            (index) => !used.includes(index),
        ),
    ];
};

const normalizePacketGroups = (value, count) => {
    const used = [];

    const groups = String(value || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((group) =>
            group
                .split(/[,;|/]+/)
                .map((item) => parseInt(item, 10) - 1)
                .filter((index) => {
                    if (
                        !Number.isInteger(index) ||
                        index < 0 ||
                        index >= count ||
                        used.includes(index)
                    ) {
                        return false;
                    }

                    used.push(index);
                    return true;
                }),
        )
        .filter((group) => group.length);

    return [
        ...groups,
        ...Array.from({ length: count }, (_, index) => index)
            .filter((index) => !used.includes(index))
            .map((index) => [index]),
    ];
};
const flattenPackets = (packets) => packets.flatMap((packet) => packet);

const copyPackets = (packets) => packets.map((packet) => [...packet]);

const compareRoutes = (cardA, cardB) => {
    const length = Math.max(cardA.route.length, cardB.route.length);

    for (let index = 0; index < length; index += 1) {
        const difference =
            (cardA.route[index] || 0) - (cardB.route[index] || 0);

        if (difference) {
            return difference;
        }
    }

    return 0;
};

class SequenceStep {
    static type = "base";
    static title = "Paso";
    static description = "";
    static defaults = {};

    static create(config = {}) {
        return {
            id: createId(),
            type: this.type,
            ...this.defaults,
            ...config,
        };
    }

    static radix() {
        return 1;
    }

    static label() {
        return this.title;
    }

    static fields() {
        return [];
    }

    static codePart(choice) {
        return String(choice + 1);
    }

    static pictogramPart(choice) {
        return String(choice + 1);
    }

    static apply(state) {
        return {
            packets: copyPackets(state.packets),
            view: null,
        };
    }
}

class DealStep extends SequenceStep {
    static type = "deal";
    static title = "Dada";
    static description =
        "Reparte por un tramo del código y permite dejar, montar o agrupar parcialmente los paquetes.";

    static defaults = {
        packets: 4,
        faceDown: true,
        collectOrder: "1,2,3,4",
        layoutId: "fila",
    };

    static radix(step) {
        return clampInt(step.packets, 4, 1, 26);
    }

    static label(step) {
        return `Dada de ${this.radix(step)}${
            step.faceDown ? " cara abajo" : ""
        }`;
    }

    static fields() {
        return [
            {
                key: "packets",
                label: "Paquetes",
                type: "number",
                min: 1,
                max: 26,
            },
            {
                key: "faceDown",
                label: "Cara abajo",
                type: "boolean",
            },
            {
                key: "collectOrder",
                label: "Montaje",
                placeholder: "1,2 3,4 · vacío = separados",
            },
        ];
    }

    static layout(step) {
        return (
            PICTOGRAM_LAYOUT_PRESETS[this.radix(step)]?.find(
                (layout) => layout.id === step.layoutId,
            ) ||
            PICTOGRAM_LAYOUT_PRESETS[this.radix(step)]?.[0] ||
            null
        );
    }

    static pictograms(step, context) {
        return {
            ...(this.layout(step)?.pictograms ||
                getPictogramsForPackets(
                    this.radix(step),
                    context.pictogramsByPackets,
                )),
            ...(step.pictograms || {}),
        };
    }

    static codePart(choice, step) {
        return getLetters(this.radix(step))[choice];
    }

    static pictogramPart(choice, step, context) {
        const letter = this.codePart(choice, step);

        return this.pictograms(step, context)[letter] || letter;
    }

    static apply(state, step, context) {
        const packetCount = this.radix(step);
        const piles = Array.from({ length: packetCount }, () => []);

        flattenPackets(state.packets).forEach((card) => {
            piles[card.route[context.routingIndex]]?.push(card);
        });

        const tablePiles = piles.map((pile) =>
            step.faceDown ? [...pile].reverse() : [...pile],
        );
        const collectGroups = normalizePacketGroups(
            step.collectOrder,
            packetCount,
        );

        return {
            packets: collectGroups.map((group) =>
                group.flatMap((index) => tablePiles[index]),
            ),
            view: {
                title: "Paquetes sobre la mesa",
                packets: tablePiles,
                labels: getLetters(packetCount),
                pictograms: this.pictograms(step, context),
                layoutRows: this.layout(step)?.rows,
                collectGroups,
            },
        };
    }
}

class BevelStep extends SequenceStep {
    static type = "bevel";
    static title = "Biselada";
    static description =
        "Separa cada paquete actual en dos grupos sin invertir su orden y permite dejarlos separados o montarlos.";

    static defaults = {
        edge: "long",
        separate: false,
        collectOrder: "1,2",
    };

    static radix() {
        return 2;
    }

    static label(step) {
        return `${step.edge === "short" ? "Biselada corta" : "Biselada larga"}${
            step.separate ? " · separar" : ""
        }`;
    }

    static fields(step, context) {
        return [
            {
                key: "edge",
                label: "Canto",
                type: "select",
                options: [
                    {
                        value: "long",
                        label: "Largo",
                        disabled:
                            step.edge !== "long" &&
                            context.usedBevelEdges.includes("long"),
                    },
                    {
                        value: "short",
                        label: "Corto",
                        disabled:
                            step.edge !== "short" &&
                            context.usedBevelEdges.includes("short"),
                    },
                ],
            },
            {
                key: "separate",
                label: "Dejar separados",
                type: "boolean",
            },
            !step.separate
                ? {
                      key: "collectOrder",
                      label: "Orden",
                      placeholder: "1,2",
                  }
                : null,
        ].filter(Boolean);
    }

    static pictograms(step) {
        return {
            A: step.edge === "short" ? "◧" : "◩",
            B: step.edge === "short" ? "◨" : "◪",
            ...(step.pictograms || {}),
        };
    }

    static codePart(choice, step) {
        return `${step.edge === "short" ? "C" : "L"}${choice + 1}`;
    }

    static pictogramPart(choice, step) {
        return this.pictograms(step)[choice === 0 ? "A" : "B"];
    }

    static apply(state, step, context) {
        const collectOrder = normalizeOrder(step.collectOrder, 2);
        const groups = state.packets.flatMap((packet) => {
            const packetGroups = [[], []];

            packet.forEach((card) => {
                packetGroups[card.route[context.routingIndex]]?.push(card);
            });

            return packetGroups;
        });
        const collectGroups = state.packets.map((_, packetIndex) =>
            collectOrder.map((index) => packetIndex * 2 + index),
        );
        const packets = step.separate
            ? groups
            : collectGroups.map((group) =>
                  group.flatMap((index) => groups[index]),
              );
        const labels = groups.map((_, index) =>
            state.packets.length > 1
                ? `${Math.floor(index / 2) + 1}${index % 2 ? "B" : "A"}`
                : index
                  ? "B"
                  : "A",
        );

        return {
            packets,
            view: {
                title: "Grupos extraídos",
                packets: groups,
                labels,
                pictograms: this.pictograms(step),
                collectGroups: step.separate ? undefined : collectGroups,
            },
        };
    }
}

class CutStep extends SequenceStep {
    static type = "cut";
    static title = "Cortar";
    static description =
        "Divide desde TOP por las posiciones indicadas y puede montar los paquetes.";

    static defaults = {
        points: "13",
        complete: false,
    };

    static label(step) {
        return `Cortar por ${step.points || "—"}${
            step.complete ? " y completar" : ""
        }`;
    }

    static fields() {
        return [
            {
                key: "points",
                label: "Cortes",
                placeholder: "13,26,39",
            },
            {
                key: "complete",
                label: "Y completar",
                type: "boolean",
            },
        ];
    }

    static apply(state, step) {
        const deck = flattenPackets(state.packets);
        const points = [...new Set(parseNumberList(step.points))]
            .filter((point) => point > 0 && point < deck.length)
            .sort((a, b) => a - b);

        const packets = [];
        let cursor = 0;

        [...points, deck.length].forEach((point) => {
            packets.push(deck.slice(cursor, point));
            cursor = point;
        });

        const collectOrder = packets.map(
            (_, index) => packets.length - index - 1,
        );

        return {
            packets: step.complete
                ? [collectOrder.flatMap((index) => packets[index])]
                : packets,
            view: {
                title: step.complete
                    ? "Paquetes del corte y montaje"
                    : "Paquetes tras los cortes",
                packets,
                labels: packets.map((_, index) => String(index + 1)),
                collectOrder: step.complete ? collectOrder : undefined,
            },
        };
    }
}

class RearmStep extends SequenceStep {
    static type = "rearm";
    static title = "Rearmar";
    static description =
        "Monta los paquetes actuales en uno o varios montones. Separa los montones con espacios.";

    static defaults = {
        collectOrder: "2,1",
    };

    static label(step) {
        return `Rearmar ${step.collectOrder || "—"}`;
    }

    static fields() {
        return [
            {
                key: "collectOrder",
                label: "Montaje",
                placeholder: "1,3,5,7 2,4,6,8",
            },
        ];
    }

    static apply(state, step) {
        const packets = copyPackets(state.packets);
        const collectGroups = /\s/.test(String(step.collectOrder || "").trim())
            ? normalizePacketGroups(step.collectOrder, packets.length)
            : [normalizeOrder(step.collectOrder, packets.length)];

        return {
            packets: collectGroups.map((group) =>
                group.flatMap((index) => packets[index] || []),
            ),
            view: {
                title: "Orden de montaje",
                packets,
                labels: packets.map((_, index) => String(index + 1)),
                collectGroups,
                warning:
                    packets.length < 2
                        ? "Solo existe un paquete. No hay nada que rearmar."
                        : "",
            },
        };
    }
}

class ReverseStep extends SequenceStep {
    static type = "reverse";
    static title = "Invertir paquete";
    static description =
        "Invierte cada paquete por separado o únicamente el paquete seleccionado.";

    static defaults = {
        target: "all",
    };

    static label(step) {
        return step.target === "all"
            ? "Invertir todos los paquetes"
            : `Invertir paquete ${step.target}`;
    }

    static fields(step, context) {
        const target = parseInt(step.target, 10);

        return [
            {
                key: "target",
                label: "Aplicar a",
                type: "select",
                value:
                    step.target === "all" ||
                    (target >= 1 && target <= context.packetCount)
                        ? step.target
                        : "all",
                options: [
                    {
                        value: "all",
                        label: "Todos los paquetes",
                    },
                    ...Array.from(
                        { length: context.packetCount },
                        (_, index) => ({
                            value: String(index + 1),
                            label: context.packetNames?.[index]
                                ? `${index + 1} · ${context.packetNames[index]}`
                                : `Paquete ${index + 1}`,
                        }),
                    ),
                ],
            },
        ];
    }

    static apply(state, step) {
        const target = parseInt(step.target, 10) - 1;
        const invertAll =
            step.target === "all" ||
            target < 0 ||
            target >= state.packets.length;

        const packets = copyPackets(state.packets).map((packet, index) =>
            invertAll || index === target ? [...packet].reverse() : packet,
        );

        return {
            packets,
            view: {
                title: invertAll
                    ? "Paquetes invertidos"
                    : `Paquete ${target + 1} invertido`,
                packets,
                labels: packets.map((_, index) => String(index + 1)),
            },
        };
    }
}

class ClockStep extends SequenceStep {
    static type = "clock";
    static title = "Reloj";
    static description = "Reparte cíclicamente y deja los paquetes separados.";

    static defaults = {
        packets: 4,
        start: 1,
        direction: "clockwise",
        faceDown: true,
    };

    static label(step) {
        return `Reloj de ${clampInt(step.packets, 4, 2, 26)}`;
    }

    static fields() {
        return [
            {
                key: "packets",
                label: "Paquetes",
                type: "number",
                min: 2,
                max: 26,
            },
            {
                key: "start",
                label: "Empieza",
                type: "number",
                min: 1,
                max: 26,
            },
            {
                key: "direction",
                label: "Sentido",
                type: "select",
                options: [
                    {
                        value: "clockwise",
                        label: "Horario",
                    },
                    {
                        value: "counterclockwise",
                        label: "Antihorario",
                    },
                ],
            },
            {
                key: "faceDown",
                label: "Cara abajo",
                type: "boolean",
            },
        ];
    }

    static apply(state, step) {
        const packetCount = clampInt(step.packets, 4, 2, 26);
        const start = clampInt(step.start, 1, 1, packetCount) - 1;
        const direction = step.direction === "counterclockwise" ? -1 : 1;

        const piles = Array.from({ length: packetCount }, () => []);

        flattenPackets(state.packets).forEach((card, index) => {
            const packetIndex =
                (start + direction * index + packetCount * 1000) % packetCount;

            piles[packetIndex].push(card);
        });

        const packets = piles.map((pile) =>
            step.faceDown ? [...pile].reverse() : [...pile],
        );

        return {
            packets,
            view: {
                title: "Paquetes del reloj",
                packets,
                labels: getLetters(packetCount),
            },
        };
    }
}

class GroupShuffleStep extends SequenceStep {
    static type = "groupShuffle";
    static title = "Mezclar paquetes";
    static description =
        "Mezcla de forma aleatoria cada paquete actual y permite montajes parciales.";

    static defaults = {
        collectOrder: "",
    };

    static label(step) {
        return step.collectOrder
            ? `Mezclar y montar ${step.collectOrder}`
            : "Mezclar paquetes separados";
    }

    static fields() {
        return [
            {
                key: "collectOrder",
                label: "Montaje",
                placeholder: "1,2 3,4 · vacío = separados",
            },
        ];
    }

    static apply(state, step) {
        const packets = copyPackets(state.packets).map((packet, index) =>
            shufflePacket(packet, Number(step.shuffleSeed || 1) + index),
        );

        const collectGroups = normalizePacketGroups(
            step.collectOrder,
            packets.length,
        );

        return {
            packets: collectGroups.map((group) =>
                group.flatMap((index) => packets[index] || []),
            ),
            view: {
                title: "Paquetes mezclados",
                packets,
                labels: packets.map((_, index) => String(index + 1)),
                collectGroups,
            },
        };
    }
}

const STEP_TYPES = [
    DealStep,
    BevelStep,
    CutStep,
    RearmStep,
    ReverseStep,
    ClockStep,
    GroupShuffleStep,
];

const STEPS = Object.fromEntries(STEP_TYPES.map((Step) => [Step.type, Step]));

const createStep = (type, config = {}) =>
    (STEPS[type] || DealStep).create(
        type === "groupShuffle" && !config.shuffleSeed
            ? {
                  ...config,
                  shuffleSeed: createRandomSeed(),
              }
            : config,
    );

const getCapacity = (sequence) =>
    sequence.reduce(
        (capacity, step) => capacity * STEPS[step.type].radix(step),
        1,
    );

const getRoutingSteps = (sequence) =>
    sequence.filter((step) => STEPS[step.type].radix(step) > 1);

const getRouteMeta = (route, sequence, pictogramsByPackets) => {
    const code = [];
    const numeric = [];
    const pictogram = [];

    getRoutingSteps(sequence).forEach((step, index) => {
        const Step = STEPS[step.type];
        const choice = route[index] || 0;
        const context = {
            pictogramsByPackets,
        };

        code.push(Step.codePart(choice, step, context));
        numeric.push(choice + 1);
        pictogram.push(Step.pictogramPart(choice, step, context));
    });

    return {
        code: code.join("·") || "—",
        numericCode: numeric.join("-") || "—",
        pictogramCode: pictogram.join(" ") || "—",
    };
};

const createRouteCards = (sequence) => {
    const radices = getRoutingSteps(sequence).map((step) =>
        STEPS[step.type].radix(step),
    );
    const routes = [];

    const build = (route, index) => {
        if (index === radices.length) {
            routes.push(route);
            return;
        }

        for (let choice = 0; choice < radices[index]; choice += 1) {
            build([...route, choice], index + 1);
        }
    };

    build([], 0);

    return routes.map((route) => ({
        id: `route-${route.join("-")}`,
        route,
        value: "",
    }));
};

const runSequence = (cards, sequence, pictogramsByPackets) => {
    let state = {
        packets: [[...cards]],
        packetNames: [""],
    };

    let routingIndex = 0;
    const snapshots = [];

    sequence.forEach((step) => {
        const Step = STEPS[step.type];
        const beforePackets = copyPackets(state.packets);
        const beforePacketNames = copyPacketNames(
            state.packetNames,
            state.packets.length,
        );

        const result = Step.apply(state, step, {
            routingIndex,
            pictogramsByPackets,
        });

        const viewPacketNames = result.view
            ? applyPacketNames(
                  ["reverse", "groupShuffle", "rearm"].includes(step.type)
                      ? copyPacketNames(
                            beforePacketNames,
                            result.view.packets.length,
                        )
                      : Array(result.view.packets.length).fill(""),
                  step.viewPacketNames,
                  result.view.packets.length,
              )
            : [];

        const packetNames = result.view?.collectGroups
            ? getGroupedPacketNames(result.view.collectGroups, viewPacketNames)
            : result.view?.packets.length === result.packets.length
              ? viewPacketNames
              : Array(result.packets.length).fill("");

        state = {
            packets: copyPackets(result.packets),
            packetNames: applyPacketNames(
                packetNames,
                step.packetNames,
                result.packets.length,
            ),
        };

        snapshots.push({
            step,
            Step,
            beforePackets,
            beforePacketNames,
            afterPackets: copyPackets(state.packets),
            afterPacketNames: copyPacketNames(
                state.packetNames,
                state.packets.length,
            ),
            view: result.view
                ? {
                      ...result.view,
                      packetNames: viewPacketNames,
                  }
                : null,
        });

        if (Step.radix(step) > 1) {
            routingIndex += 1;
        }
    });

    return {
        snapshots,
        finalCards: flattenPackets(state.packets),
        finalPackets: copyPackets(state.packets),
        finalPacketNames: copyPacketNames(
            state.packetNames,
            state.packets.length,
        ),
    };
};

const analyzeOrder = (cards, sequence, pictogramsByPackets) => ({
    canonical: runSequence(cards, sequence, pictogramsByPackets),
    deterministic: true,
});

const buildModel = (sequence, values, pictogramsByPackets) => {
    const capacity = getCapacity(sequence);

    if (capacity > MAX_POSITIONS) {
        return {
            capacity,
            blocked: true,
            deterministic: false,
            rows: [],
            assignedCards: [],
            simulation: null,
        };
    }

    const safeValues = Array.from({ length: capacity }, (_, index) =>
        String(values[index] || ""),
    );

    const routeCards = createRouteCards(sequence).map((card, sourceIndex) => ({
        ...card,
        sourceIndex,
        value: safeValues[sourceIndex],
    }));

    const fullAnalysis = analyzeOrder(
        routeCards,
        sequence,
        pictogramsByPackets,
    );

    const fullRows = fullAnalysis.canonical.finalCards.map((card, index) => ({
        position: index + 1,
        sourceIndex: card.sourceIndex,
        routeCard: card,
        ...getRouteMeta(card.route, sequence, pictogramsByPackets),
    }));

    let relativePosition = 0;

    const assignedCards = routeCards
        .filter((card) => card.value.trim())
        .map((card) => ({
            ...card,
            id: `assigned-${card.id}`,
            relativePosition: ++relativePosition,
            ...getRouteMeta(card.route, sequence, pictogramsByPackets),
        }));

    const activeAnalysis = analyzeOrder(
        assignedCards,
        sequence,
        pictogramsByPackets,
    );

    const simulation = activeAnalysis.canonical;

    const finalPositions = new Map(
        simulation.finalCards.map((card, index) => [card.id, index + 1]),
    );

    const assignedBySourceIndex = new Map(
        assignedCards.map((card) => [card.sourceIndex, card]),
    );

    return {
        capacity,
        blocked: false,
        deterministic:
            fullAnalysis.deterministic && activeAnalysis.deterministic,
        assignedCards,
        simulation,
        rows: fullRows.map((row) => {
            const assigned = assignedBySourceIndex.get(row.sourceIndex);

            return {
                ...row,
                value: safeValues[row.sourceIndex],
                assigned,
                relativePosition: assigned?.relativePosition || "",
                finalPosition: assigned
                    ? finalPositions.get(assigned.id) || ""
                    : "",
            };
        }),
    };
};

const createDefaultSequence = (deals, packets, orientation) => {
    const safeDeals = clampInt(deals, 1, 1, 12);
    const safePackets = clampInt(packets, 1, 1, 26);

    const sequence = Array.from({ length: safeDeals }, () =>
        createStep("deal", {
            packets: safePackets,
            collectOrder: Array.from(
                { length: safePackets },
                (_, index) => index + 1,
            ).join(","),
            layoutId: PICTOGRAM_LAYOUT_PRESETS[safePackets]?.[0]?.id || "fila",
        }),
    );

    const mustReverse =
        (orientation === "cara" && safeDeals % 2 === 0) ||
        (orientation === "dorso" && safeDeals % 2 === 1);

    if (mustReverse) {
        sequence.push(createStep("reverse"));
    }

    return sequence;
};

const ConfigField = ({ field, value, onChange }) => {
    if (field.type === "boolean") {
        return (
            <FormControlLabel
                control={
                    <Checkbox
                        checked={Boolean(value)}
                        onChange={(event) => onChange(event.target.checked)}
                    />
                }
                label={field.label}
            />
        );
    }

    if (field.type === "select") {
        return (
            <FormControl size="small" sx={{ minWidth: 145 }}>
                <InputLabel>{field.label}</InputLabel>

                <Select
                    value={value}
                    label={field.label}
                    onChange={(event) => onChange(event.target.value)}
                >
                    {field.options.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    return (
        <TextField
            size="small"
            label={field.label}
            type={field.type || "text"}
            value={value}
            placeholder={field.placeholder}
            inputProps={{
                min: field.min,
                max: field.max,
            }}
            onChange={(event) =>
                onChange(
                    field.type === "number"
                        ? clampInt(
                              event.target.value,
                              field.min || 1,
                              field.min,
                              field.max,
                          )
                        : event.target.value,
                )
            }
            sx={{
                width: field.type === "number" ? 100 : 145,
            }}
        />
    );
};

const MiniCard = ({
    card,
    position,
    pilePosition,
    className,
    inPile = false,
}) => {
    const cardColor = getCardTextColor(card.value);

    return (
        <Paper
            className={className}
            variant="outlined"
            title={`${card.value || "Sin carta"} · ${
                card.code || "Sin código"
            }`}
            sx={{
                position: "relative",
                width: 82,
                minWidth: 82,
                height: 44,
                px: 0.75,
                py: 0.45,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                boxSizing: "border-box",
                borderRadius: 1.4,
                borderLeft: "3px solid",
                borderLeftColor: cardColor,
                bgcolor: "background.paper",
                boxShadow: 1,
                overflow: "hidden",
                pointerEvents: inPile ? "none" : "auto",
                transition:
                    "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                "&:hover": inPile
                    ? undefined
                    : {
                          transform: "translateY(-2px)",
                          boxShadow: 4,
                          borderColor: "primary.main",
                      },
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    lineHeight: 1.1,
                    fontSize: 12,
                    color: cardColor,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {card.value || "—"}
            </Typography>

            <Stack direction="row" spacing={0.4} alignItems="baseline">
                <Typography
                    variant="caption"
                    sx={{
                        minWidth: 0,
                        lineHeight: 1.1,
                        fontFamily: "monospace",
                        fontSize: 9,
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {position ? `#${position} · ` : ""}
                    {card.code || "—"}
                </Typography>

                {pilePosition && (
                    <Typography
                        variant="caption"
                        sx={{
                            flexShrink: 0,
                            lineHeight: 1,
                            fontFamily: "monospace",
                            fontSize: 8,
                            color: "text.disabled",
                        }}
                    >
                        ({pilePosition})
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
};

const Pile = ({
    packet,
    packetName = "",
    onPacketNameChange,
    showPacketName = false,
    label,
    pictogram,
    index,
    faceDown = false,
}) => {
    const offset =
        packet.length <= 1
            ? 0
            : Math.max(6, Math.min(16, 150 / (packet.length - 1)));

    const topCard = packet[0];
    const bottomCard = packet[packet.length - 1];

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 0.75,
                width: 112,
                minWidth: 112,
                borderRadius: 1.6,
                bgcolor: "background.default",
                boxSizing: "border-box",
                overflow: "visible",
            }}
        >
            <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        minWidth: 0,
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {pictogram ? `${pictogram} ` : ""}
                    {label || index + 1}
                </Typography>

                <Box
                    component="span"
                    sx={{
                        px: 0.55,
                        py: 0.1,
                        borderRadius: 5,
                        bgcolor: faceDown ? "grey.800" : "action.selected",
                        color: faceDown ? "common.white" : "text.secondary",
                        fontSize: 8,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                    }}
                >
                    {faceDown ? "DORSO ↑" : "CARA ↑"}
                </Box>
            </Stack>

            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    mb: 0.35,
                    color: "primary.main",
                    fontSize: 9,
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                TOP ↑ · {topCard?.value || topCard?.code || "—"}
            </Typography>

            <Box
                sx={{
                    position: "relative",
                    height: packet.length
                        ? 44 + (packet.length - 1) * offset
                        : 40,
                }}
            >
                {packet.length ? (
                    packet.map((card, cardIndex) => (
                        <Box
                            key={`${card.id}-${index}-${cardIndex}`}
                            sx={{
                                position: "absolute",
                                top: cardIndex * offset,
                                left: "50%",
                                width: 82,
                                height:
                                    cardIndex === packet.length - 1
                                        ? 44
                                        : offset,
                                transform: "translateX(-50%)",
                                zIndex: packet.length - cardIndex,
                                "&:hover": {
                                    zIndex: packet.length + 1,
                                },
                                "&:hover .pile-card": {
                                    transform: "translateY(-2px)",
                                    boxShadow: 4,
                                    borderColor: "primary.main",
                                },
                            }}
                        >
                            <MiniCard
                                card={card}
                                pilePosition={cardIndex + 1}
                                className="pile-card"
                                inPile
                            />
                        </Box>
                    ))
                ) : (
                    <Box
                        sx={{
                            height: "100%",
                            display: "grid",
                            placeItems: "center",
                            border: "1px dashed",
                            borderColor: "divider",
                            borderRadius: 1.2,
                            color: "text.disabled",
                            fontSize: 10,
                        }}
                    >
                        Vacío
                    </Box>
                )}
            </Box>

            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    mt: 0.35,
                    color: "text.secondary",
                    fontSize: 9,
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                BOTTOM ↓ · {bottomCard?.value || bottomCard?.code || "—"}
            </Typography>
            {showPacketName && (
                <TextField
                    fullWidth
                    size="small"
                    variant="standard"
                    value={packetName}
                    placeholder="Nombre"
                    inputProps={{
                        maxLength: 40,
                        readOnly: !onPacketNameChange,
                    }}
                    onChange={(event) =>
                        onPacketNameChange?.(event.target.value)
                    }
                    sx={{
                        mb: 0.5,
                        "& .MuiInputBase-input": {
                            py: 0.15,
                            fontSize: 10,
                            fontWeight: packetName ? 700 : 400,
                        },
                    }}
                />
            )}
        </Paper>
    );
};

const PacketView = ({
    title,
    packets,
    packetNames = [],
    onPacketNameChange,
    labels = [],
    pictograms = {},
    layoutRows,
    collectOrder,
    collectGroups,
    faceDown = false,
    warning = "",
}) => {
    const packetsByLabel = new Map(
        labels.map((label, index) => [
            label,
            {
                packet: packets[index] || [],
                index,
            },
        ]),
    );

    const renderPile = (label, packet, index) => (
        <Pile
            key={`${title}-${label}-${index}`}
            packet={packet || []}
            packetName={packetNames[index] || ""}
            onPacketNameChange={
                onPacketNameChange
                    ? (value) => onPacketNameChange(index, value)
                    : undefined
            }
            showPacketName={packets.length > 1}
            label={label}
            pictogram={pictograms[label]}
            index={index}
            faceDown={faceDown}
        />
    );

    return (
        <Box sx={{ minWidth: 0 }}>
            <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                flexWrap="wrap"
            >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>

                <Typography
                    variant="caption"
                    sx={{
                        px: 0.6,
                        py: 0.1,
                        borderRadius: 5,
                        bgcolor: faceDown ? "grey.800" : "action.selected",
                        color: faceDown ? "common.white" : "text.secondary",
                        fontSize: 9,
                        fontWeight: 700,
                    }}
                >
                    {faceDown ? "CARA ABAJO · DORSO VISIBLE" : "CARA ARRIBA"}
                </Typography>
            </Stack>

            {warning && (
                <Alert
                    severity="warning"
                    sx={{
                        mt: 0.6,
                        py: 0,
                        "& .MuiAlert-message": {
                            py: 0.4,
                            fontSize: 11,
                        },
                    }}
                >
                    {warning}
                </Alert>
            )}

            {layoutRows?.length ? (
                <Stack
                    spacing={1}
                    sx={{
                        pt: 0.75,
                        overflowX: "auto",
                    }}
                >
                    {layoutRows.map((row, rowIndex) => (
                        <Stack
                            key={rowIndex}
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            alignItems="flex-start"
                        >
                            {row.map((label, cellIndex) => {
                                const item = packetsByLabel.get(label);

                                return label && item ? (
                                    renderPile(label, item.packet, item.index)
                                ) : (
                                    <Box
                                        key={cellIndex}
                                        sx={{
                                            width: 108,
                                            minWidth: 108,
                                        }}
                                    />
                                );
                            })}
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        overflowX: "auto",
                        pt: 0.75,
                    }}
                >
                    {packets.map((packet, index) =>
                        renderPile(
                            labels[index] || String(index + 1),
                            packet,
                            index,
                        ),
                    )}
                </Box>
            )}

            {(collectGroups?.length || collectOrder?.length > 1) && (
                <Typography variant="caption" color="text.secondary">
                    Montaje:{" "}
                    {collectGroups?.length
                        ? collectGroups
                              .map((group) =>
                                  group
                                      .map(
                                          (index) => labels[index] || index + 1,
                                      )
                                      .join(" → "),
                              )
                              .join(" | ")
                        : collectOrder
                              .map((index) => labels[index] || index + 1)
                              .join(" → ")}
                </Typography>
            )}
        </Box>
    );
};

const RecipeTable = ({ rows, onChange, onMove }) => {
    const [dragOverIndex, setDragOverIndex] = useState(null);
    return (
        <Table
            size="small"
            sx={{
                width: "max-content",
                minWidth: "100%",
                "& .MuiTableCell-root": {
                    py: 0.15,
                    px: 0.5,
                    fontSize: 11,
                    whiteSpace: "nowrap",
                },
            }}
        >
            <TableHead>
                <TableRow>
                    {[
                        "#",
                        "#R",
                        "Código",
                        "Num",
                        "Picto",
                        "Carta",
                        "Final",
                    ].map((label) => (
                        <TableCell
                            key={label}
                            sx={{
                                fontWeight: 700,
                            }}
                        >
                            {label}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>

            <TableBody>
                {rows.map((row) => {
                    const meta = row.assigned || row;
                    const changedCode =
                        row.assigned && row.assigned.code !== row.code;

                    return (
                        <TableRow
                            key={row.position}
                            onDragOver={(event) => {
                                event.preventDefault();
                                event.dataTransfer.dropEffect = "move";
                                setDragOverIndex(row.sourceIndex);
                            }}
                            onDragLeave={(event) => {
                                if (
                                    !event.currentTarget.contains(
                                        event.relatedTarget,
                                    )
                                ) {
                                    setDragOverIndex(null);
                                }
                            }}
                            onDrop={(event) => {
                                event.preventDefault();

                                const sourceIndex = event.dataTransfer.getData(
                                    "application/x-radical-card",
                                );

                                setDragOverIndex(null);

                                if (sourceIndex !== "") {
                                    onMove(
                                        Number(sourceIndex),
                                        row.sourceIndex,
                                    );
                                }
                            }}
                            sx={(theme) => ({
                                "& > td": {
                                    boxShadow:
                                        dragOverIndex === row.sourceIndex
                                            ? `inset 0 2px 0 ${theme.palette.primary.main}`
                                            : "none",
                                },
                            })}
                        >
                            <TableCell>{row.position}</TableCell>

                            <TableCell>{row.relativePosition}</TableCell>

                            <TableCell
                                sx={{
                                    fontFamily: "monospace",
                                }}
                            >
                                <Tooltip
                                    title={
                                        changedCode
                                            ? `Código base: ${row.code}`
                                            : "Código calculado"
                                    }
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            fontWeight: changedCode ? 700 : 400,
                                        }}
                                    >
                                        {meta.code}
                                    </Box>
                                </Tooltip>
                            </TableCell>

                            <TableCell
                                sx={{
                                    fontFamily: "monospace",
                                }}
                            >
                                {meta.numericCode}
                            </TableCell>

                            <TableCell>{meta.pictogramCode}</TableCell>

                            <TableCell>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                >
                                    <Box
                                        component="span"
                                        draggable
                                        title="Arrastra para cambiar la posición"
                                        onDragStart={(event) => {
                                            event.dataTransfer.setData(
                                                "application/x-radical-card",
                                                String(row.sourceIndex),
                                            );
                                            event.dataTransfer.effectAllowed =
                                                "move";
                                        }}
                                        onDragEnd={() => {
                                            setDragOverIndex(null);
                                        }}
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            color: "text.disabled",
                                            cursor: "grab",
                                            "&:active": {
                                                cursor: "grabbing",
                                            },
                                            "&:hover": {
                                                color: "text.secondary",
                                            },
                                        }}
                                    >
                                        <Icons.Drag sx={{ fontSize: 16 }} />
                                    </Box>

                                    <TextField
                                        variant="standard"
                                        value={row.value}
                                        onChange={(event) =>
                                            onChange(
                                                row.sourceIndex,
                                                event.target.value,
                                            )
                                        }
                                        placeholder="Carta"
                                        sx={{
                                            width: 90,
                                            "& .MuiInputBase-input": {
                                                py: 0.1,
                                                fontSize: 11,
                                                color: getCardTextColor(
                                                    row.value,
                                                ),
                                                fontWeight: 500,
                                            },
                                        }}
                                    />
                                </Stack>
                            </TableCell>

                            <TableCell>{row.finalPosition}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

const SCHEMA_VERSION = 2;

const RadicalAdvancedMode = ({
    open,
    onClose,
    initialRecipeValues = [],
    defaultDeals = 2,
    defaultPackets = 8,
    deckOrientation = "cara",
    pictogramsByPackets = {},
    presets = [],
    onSavePreset,
    onDeletePreset,
    onRenamePreset,
    initialPresetId = "",
}) => {
    const [sequence, setSequence] = useState([]);
    const [values, setValues] = useState([]);
    const [current, setCurrent] = useState(0);

    const [adding, setAdding] = useState(false);
    const [newType, setNewType] = useState("deal");
    const [newPosition, setNewPosition] = useState("after");

    const [selectedPresetId, setSelectedPresetId] = useState(initialPresetId);
    const [recipeDescription, setRecipeDescription] = useState("");
    const [recipeDeckOrientation, setRecipeDeckOrientation] =
        useState(deckOrientation);
    const [recipePictogramsByPackets, setRecipePictogramsByPackets] =
        useState(pictogramsByPackets);

    const [loadedRecipeSnapshot, setLoadedRecipeSnapshot] = useState("");
    const [isSavePresetDialogOpen, setIsSavePresetDialogOpen] = useState(false);
    const [isRenamePresetDialogOpen, setIsRenamePresetDialogOpen] =
        useState(false);
    const [isCloseConfirmationOpen, setIsCloseConfirmationOpen] =
        useState(false);
    const [confirmationDialog, setConfirmationDialog] = useState("");
    const [pendingPresetId, setPendingPresetId] = useState(null);
    const [pendingStepIndex, setPendingStepIndex] = useState(null);
    const [presetNameDraft, setPresetNameDraft] = useState("");
    const [isPictogramDialogOpen, setIsPictogramDialogOpen] = useState(false);

    const wasOpen = useRef(false);

    const selectedPreset = presets.find(
        (preset) => preset.id === selectedPresetId,
    );

    const currentPresetData = useMemo(
        () =>
            getAdvancedRecipeData(
                recipeDescription,
                sequence,
                values,
                recipeDeckOrientation,
                recipePictogramsByPackets,
            ),
        [
            recipeDeckOrientation,
            recipeDescription,
            recipePictogramsByPackets,
            sequence,
            values,
        ],
    );

    const isSelectedPresetModified =
        Boolean(loadedRecipeSnapshot) &&
        JSON.stringify(currentPresetData) !== loadedRecipeSnapshot;

    const loadPreset = (preset) => {
        const data = preset.data || {};
        const nextSequence = (Array.isArray(data.sequence) ? data.sequence : [])
            .filter((step) => STEPS[step?.type])
            .map((step) => ({
                ...step,
                ...(step.type === "groupShuffle" && !step.shuffleSeed
                    ? {
                          shuffleSeed: createRandomSeed(),
                      }
                    : {}),
                id: createId(),
            }));
        const nextValues = Array.isArray(data.values)
            ? data.values.map((value) => String(value ?? ""))
            : [];
        const nextDescription = preset.description || "";
        const nextDeckOrientation = data.deckOrientation || "cara";
        const nextPictogramsByPackets = data.pictogramsByPackets || {};

        setSequence(nextSequence);
        setValues(nextValues);
        setRecipeDescription(nextDescription);
        setRecipeDeckOrientation(nextDeckOrientation);
        setRecipePictogramsByPackets(nextPictogramsByPackets);
        setSelectedPresetId(preset.id);
        setCurrent(0);
        setLoadedRecipeSnapshot(
            JSON.stringify(
                getAdvancedRecipeData(
                    nextDescription,
                    nextSequence,
                    nextValues,
                    nextDeckOrientation,
                    nextPictogramsByPackets,
                ),
            ),
        );
    };

    const savePreset = (overwrite = false) => {
        const name = overwrite
            ? selectedPreset?.name
            : window.prompt(
                  "Nombre de la receta avanzada",
                  selectedPreset?.name || "",
              );

        if (!name?.trim()) {
            return;
        }

        const id =
            overwrite && selectedPreset
                ? selectedPreset.id
                : createUserPresetId();

        onSavePreset?.({
            id,
            name: name.trim(),
            ...currentPresetData,
            recipeType: "advanced",
            schemaVersion: SCHEMA_VERSION,
            isBuiltIn: false,
        });

        setSelectedPresetId(id);
        setLoadedRecipeSnapshot(JSON.stringify(currentPresetData));
        setIsSavePresetDialogOpen(false);
    };

    const reset = () => {
        setIsPictogramDialogOpen(false);

        const nextSequence = createDefaultSequence(
            defaultDeals,
            defaultPackets,
            deckOrientation,
        );
        const sourceValues = Array(getCapacity(nextSequence)).fill("");

        analyzeOrder(
            createRouteCards(nextSequence).map((card, sourceIndex) => ({
                ...card,
                sourceIndex,
            })),
            nextSequence,
            pictogramsByPackets,
        ).canonical.finalCards.forEach((card, index) => {
            sourceValues[card.sourceIndex] = String(
                initialRecipeValues[index] || "",
            );
        });

        setSequence(nextSequence);
        setValues(sourceValues);
        setCurrent(0);
        setSelectedPresetId("");
        setRecipeDescription("");
        setRecipeDeckOrientation(deckOrientation);
        setRecipePictogramsByPackets(pictogramsByPackets);
        setLoadedRecipeSnapshot(
            JSON.stringify(
                getAdvancedRecipeData(
                    "",
                    nextSequence,
                    sourceValues,
                    deckOrientation,
                    pictogramsByPackets,
                ),
            ),
        );
    };

    const loadPresetById = (presetId) => {
        const preset = presets.find((item) => item.id === presetId);

        if (preset) {
            loadPreset(preset);
        } else {
            reset();
        }
    };

    const handlePresetSelectChange = (event) => {
        const presetId = event.target.value;

        if (presetId === selectedPresetId) {
            return;
        }

        if (isSelectedPresetModified) {
            setPendingPresetId(presetId);
            return;
        }

        loadPresetById(presetId);
    };

    const handleClose = () => {
        if (isSelectedPresetModified) {
            setIsCloseConfirmationOpen(true);
            return;
        }

        onClose();
    };

    const handleOpenRenamePresetDialog = () => {
        if (!selectedPreset) {
            return;
        }

        setPresetNameDraft(selectedPreset.name);
        setIsRenamePresetDialogOpen(true);
    };

    const handleRenamePreset = () => {
        const name = presetNameDraft.trim();

        if (!name || !selectedPreset) {
            return;
        }

        onRenamePreset?.(selectedPreset.id, name);
        setIsRenamePresetDialogOpen(false);
    };

    const handleDeletePreset = () => {
        if (!selectedPreset) {
            return;
        }

        onDeletePreset?.(selectedPreset.id);
        reset();
    };

    const handleClearCurrentCards = () => {
        setValues(Array(getCapacity(sequence)).fill(""));
    };

    useEffect(() => {
        if (open && !wasOpen.current) {
            loadPresetById(initialPresetId);
        }

        wasOpen.current = open;
    }, [open, initialPresetId]);

    const model = useMemo(
        () => buildModel(sequence, values, recipePictogramsByPackets),
        [sequence, values, recipePictogramsByPackets],
    );

    const snapshot = model.simulation?.snapshots[current];

    const step = sequence[current];

    const Step = step ? STEPS[step.type] : null;
    const currentStepPictograms =
        step?.type === "deal"
            ? DealStep.pictograms(step, {
                  pictogramsByPackets: recipePictogramsByPackets,
              })
            : step?.type === "bevel"
              ? BevelStep.pictograms(step)
              : {};

    const beforeFaceDown = sequence
        .slice(0, current)
        .reduce(
            (faceDown, item) =>
                typeof item.faceDown === "boolean" ? item.faceDown : faceDown,
            recipeDeckOrientation === "dorso",
        );

    const afterFaceDown =
        typeof step?.faceDown === "boolean" ? step.faceDown : beforeFaceDown;

    const finalFaceDown = sequence.reduce(
        (faceDown, item) =>
            typeof item.faceDown === "boolean" ? item.faceDown : faceDown,
        recipeDeckOrientation === "dorso",
    );

    const usedBevelEdges = sequence
        .filter((item, index) => item.type === "bevel" && index !== current)
        .map((item) => item.edge);

    const allBevelEdges = sequence
        .filter((item) => item.type === "bevel")
        .map((item) => item.edge);

    const splitIndex = Math.ceil(model.rows.length / 2);

    const updateStep = (patch, stepIndex = current) => {
        setSequence((previous) =>
            previous.map((item, index) =>
                index === stepIndex ? { ...item, ...patch } : item,
            ),
        );
    };

    const moveStep = (sourceIndex, targetIndex) => {
        setSequence((previous) => {
            const next = [...previous];
            const [moving] = next.splice(sourceIndex, 1);

            next.splice(targetIndex, 0, moving);

            return next;
        });

        setCurrent(targetIndex);
    };

    const removeStep = (index) => {
        setSequence((previous) =>
            previous.filter((_, itemIndex) => itemIndex !== index),
        );

        setCurrent((previous) =>
            Math.max(
                0,
                Math.min(
                    previous > index ? previous - 1 : previous,
                    sequence.length - 2,
                ),
            ),
        );
    };

    const openAddDialog = () => {
        setNewType("deal");
        setNewPosition("after");
        setAdding(true);
    };

    const addStep = () => {
        const config =
            newType === "bevel"
                ? {
                      edge: !allBevelEdges.includes("long") ? "long" : "short",
                  }
                : {};

        const insertIndex =
            sequence.length > 0
                ? current + (newPosition === "after" ? 1 : 0)
                : 0;

        const nextStep = createStep(newType, config);

        setSequence((previous) => [
            ...previous.slice(0, insertIndex),
            nextStep,
            ...previous.slice(insertIndex),
        ]);

        setCurrent(insertIndex);
        setAdding(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    height: "calc(100vh - 32px)",
                    maxHeight: "calc(100vh - 32px)",
                },
            }}
        >
            <DialogTitle sx={{ py: 1 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography variant="h6">
                            Modo avanzado - ⚠️ Beta
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                            Secuencias asimétricas y operaciones modulares
                        </Typography>
                    </Box>

                    <Stack direction="row">
                        <Tooltip title="Recargar desde el modo normal">
                            <IconButton
                                onClick={() => {
                                    if (isSelectedPresetModified) {
                                        setPendingPresetId("");
                                        return;
                                    }

                                    reset();
                                }}
                            >
                                <RestartAltIcon />
                            </IconButton>
                        </Tooltip>

                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 1.25 }}>
                <Stack spacing={1.25}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={0.5}
                        alignItems={{ xs: "stretch", sm: "center" }}
                    >
                        <FormControl size="small" fullWidth>
                            <InputLabel id="advanced-preset-select-label">
                                Receta avanzada
                            </InputLabel>

                            <Select
                                labelId="advanced-preset-select-label"
                                label="Receta avanzada"
                                value={selectedPresetId}
                                onChange={handlePresetSelectChange}
                            >
                                <MenuItem value="">
                                    Desde el modo normal
                                </MenuItem>

                                {presets.map((preset) => (
                                    <MenuItem key={preset.id} value={preset.id}>
                                        {preset.name}
                                        {preset.id === selectedPresetId &&
                                        isSelectedPresetModified
                                            ? " · Modificado"
                                            : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Stack direction="row" alignItems="center">
                            <Tooltip title="Limpiar cartas asignadas" arrow>
                                <span>
                                    <IconButton
                                        disabled={!values.some(Boolean)}
                                        onClick={() =>
                                            setConfirmationDialog("clearCards")
                                        }
                                    >
                                        <Icons.Backspace />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Editar nombre" arrow>
                                <span>
                                    <IconButton
                                        disabled={!selectedPreset}
                                        onClick={handleOpenRenamePresetDialog}
                                    >
                                        <Icons.Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Eliminar receta seleccionada" arrow>
                                <span>
                                    <IconButton
                                        disabled={!selectedPreset}
                                        onClick={() =>
                                            setConfirmationDialog(
                                                "deletePreset",
                                            )
                                        }
                                    >
                                        <Icons.Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Stack>
                    <Paper
                        variant="outlined"
                        sx={{
                            px: 1,
                            py: 0.65,
                        }}
                    >
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            <Typography variant="body2">
                                <strong>Posiciones:</strong> {model.capacity}
                            </Typography>

                            <Typography variant="body2">
                                <strong>Asignadas:</strong>{" "}
                                {model.assignedCards.length}
                            </Typography>

                            <Typography variant="body2">
                                <strong>Pasos:</strong> {sequence.length}
                            </Typography>

                            <Typography variant="body2">
                                <strong>Resultado:</strong>{" "}
                                {model.deterministic
                                    ? "estable en las pruebas"
                                    : "variable"}
                            </Typography>
                        </Stack>
                    </Paper>

                    {model.blocked && (
                        <Alert severity="error">
                            La secuencia crea {model.capacity} posiciones. El
                            límite del editor es {MAX_POSITIONS}.
                        </Alert>
                    )}

                    {!model.blocked && !model.deterministic && (
                        <Alert severity="warning">
                            El resultado depende del orden inicial o de las
                            mezclas internas. Se muestra una simulación
                            canónica, pero no una posición garantizada.
                        </Alert>
                    )}

                    {!model.blocked && (
                        <Paper variant="outlined" sx={{ p: 0.7 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                }}
                            >
                                Receta final
                            </Typography>

                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Los huecos no cuentan físicamente; #R y el
                                código se recalculan al cambiar la secuencia.
                            </Typography>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        lg: "1fr 1fr",
                                    },
                                    gap: 2,
                                    mt: 0.5,
                                }}
                            >
                                {[
                                    model.rows.slice(0, splitIndex),
                                    model.rows.slice(splitIndex),
                                ].map((rows, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            overflowX: "auto",
                                        }}
                                    >
                                        <RecipeTable
                                            rows={rows}
                                            onChange={(position, value) =>
                                                setValues((previous) => {
                                                    const next = [...previous];

                                                    next[position] =
                                                        normalizeCardText(
                                                            value,
                                                        );

                                                    return next;
                                                })
                                            }
                                            onMove={(
                                                sourceIndex,
                                                targetIndex,
                                            ) =>
                                                setValues((previous) => {
                                                    if (
                                                        sourceIndex ===
                                                        targetIndex
                                                    ) {
                                                        return previous;
                                                    }

                                                    const next = [...previous];
                                                    const indexes =
                                                        model.rows.map(
                                                            (row) =>
                                                                row.sourceIndex,
                                                        );
                                                    const sourcePosition =
                                                        indexes.indexOf(
                                                            sourceIndex,
                                                        );
                                                    const targetPosition =
                                                        indexes.indexOf(
                                                            targetIndex,
                                                        );

                                                    if (
                                                        sourcePosition === -1 ||
                                                        targetPosition === -1
                                                    ) {
                                                        return previous;
                                                    }

                                                    const orderedValues =
                                                        indexes.map(
                                                            (index) =>
                                                                next[index] ||
                                                                "",
                                                        );
                                                    const [moving] =
                                                        orderedValues.splice(
                                                            sourcePosition,
                                                            1,
                                                        );

                                                    orderedValues.splice(
                                                        sourcePosition <
                                                            targetPosition
                                                            ? targetPosition - 1
                                                            : targetPosition,
                                                        0,
                                                        moving,
                                                    );
                                                    indexes.forEach(
                                                        (index, position) => {
                                                            next[index] =
                                                                orderedValues[
                                                                    position
                                                                ];
                                                        },
                                                    );

                                                    return next;
                                                })
                                            }
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    )}

                    <Paper variant="outlined" sx={{ p: 0.7 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                }}
                            >
                                Secuencia
                            </Typography>

                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={openAddDialog}
                                sx={{
                                    textTransform: "none",
                                }}
                            >
                                Agregar paso
                            </Button>
                        </Stack>

                        <Box
                            sx={{
                                display: "flex",
                                gap: 0.75,
                                overflowX: "auto",
                                py: 0.5,
                            }}
                        >
                            {sequence.map((item, index) => {
                                const ItemStep = STEPS[item.type];

                                return (
                                    <Paper
                                        key={item.id}
                                        variant="outlined"
                                        onClick={() => setCurrent(index)}
                                        sx={{
                                            px: 0.7,
                                            py: 0.4,
                                            minWidth: 150,
                                            flexShrink: 0,
                                            cursor: "pointer",
                                            borderColor:
                                                index === current
                                                    ? "primary.main"
                                                    : "divider",
                                            bgcolor:
                                                index === current
                                                    ? "action.selected"
                                                    : "background.paper",
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    flex: 1,
                                                    fontWeight:
                                                        index === current
                                                            ? 700
                                                            : 500,
                                                }}
                                            >
                                                {index + 1}.{" "}
                                                {ItemStep.label(item)}
                                            </Typography>

                                            <IconButton
                                                size="small"
                                                disabled={!index}
                                                onClick={(event) => {
                                                    event.stopPropagation();

                                                    moveStep(index, index - 1);
                                                }}
                                            >
                                                <ArrowBackIcon
                                                    sx={{
                                                        fontSize: 15,
                                                    }}
                                                />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                disabled={
                                                    index ===
                                                    sequence.length - 1
                                                }
                                                onClick={(event) => {
                                                    event.stopPropagation();

                                                    moveStep(index, index + 1);
                                                }}
                                            >
                                                <ArrowForwardIcon
                                                    sx={{
                                                        fontSize: 15,
                                                    }}
                                                />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setPendingStepIndex(index);
                                                }}
                                            >
                                                <DeleteIcon
                                                    sx={{
                                                        fontSize: 15,
                                                    }}
                                                />
                                            </IconButton>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Box>

                        {step && Step ? (
                            <Stack
                                direction={{
                                    xs: "column",
                                    md: "row",
                                }}
                                spacing={1}
                                alignItems={{
                                    xs: "stretch",
                                    md: "center",
                                }}
                            >
                                <Tooltip title={Step.description}>
                                    <Box
                                        sx={{
                                            minWidth: 180,
                                            cursor: "help",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 700,
                                            }}
                                        >
                                            {Step.label(step)}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Paso {current + 1}
                                        </Typography>
                                    </Box>
                                </Tooltip>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                >
                                    {Step.fields(step, {
                                        usedBevelEdges,
                                        packetCount:
                                            snapshot?.beforePackets.length || 1,
                                        packetNames:
                                            snapshot?.beforePacketNames || [],
                                    }).map((field) => (
                                        <ConfigField
                                            key={field.key}
                                            field={field}
                                            value={
                                                field.value ?? step[field.key]
                                            }
                                            onChange={(value) =>
                                                updateStep({
                                                    [field.key]: value,
                                                    ...(step.type === "deal" &&
                                                    field.key === "packets"
                                                        ? {
                                                              layoutId:
                                                                  PICTOGRAM_LAYOUT_PRESETS[
                                                                      value
                                                                  ]?.[0]?.id ||
                                                                  "fila",
                                                              pictograms: {},
                                                          }
                                                        : {}),
                                                    ...(PACKET_NAME_RESET_FIELDS.has(
                                                        field.key,
                                                    )
                                                        ? {
                                                              packetNames: {},
                                                              viewPacketNames:
                                                                  {},
                                                          }
                                                        : {}),
                                                })
                                            }
                                        />
                                    ))}
                                    {["deal", "bevel"].includes(step.type) && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() =>
                                                setIsPictogramDialogOpen(true)
                                            }
                                        >
                                            Pictogramas
                                        </Button>
                                    )}

                                    {step.type === "groupShuffle" && (
                                        <Button
                                            size="small"
                                            startIcon={<RestartAltIcon />}
                                            onClick={() =>
                                                updateStep({
                                                    shuffleSeed:
                                                        createRandomSeed(),
                                                })
                                            }
                                        >
                                            Volver a mezclar
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        ) : (
                            <Alert severity="info">
                                Agrega un paso para comenzar.
                            </Alert>
                        )}
                    </Paper>

                    {!model.blocked && snapshot && (
                        <Paper variant="outlined" sx={{ p: 0.8 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 0.6,
                                }}
                            >
                                Estado del paso {current + 1}:{" "}
                                {snapshot.Step.label(snapshot.step)}
                            </Typography>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        lg: snapshot.view?.packets
                                            ? "repeat(3, max-content)"
                                            : "repeat(2, max-content)",
                                    },
                                    justifyContent: "start",
                                    alignItems: "start",
                                    gap: 5,
                                    overflowX: "auto",
                                }}
                            >
                                <PacketView
                                    title="Antes"
                                    packets={snapshot.beforePackets}
                                    packetNames={snapshot.beforePacketNames}
                                    onPacketNameChange={
                                        current > 0
                                            ? (index, value) =>
                                                  updateStep(
                                                      {
                                                          packetNames: {
                                                              ...(sequence[
                                                                  current - 1
                                                              ].packetNames ||
                                                                  {}),
                                                              [index]: value,
                                                          },
                                                      },
                                                      current - 1,
                                                  )
                                            : undefined
                                    }
                                    faceDown={beforeFaceDown}
                                />

                                {snapshot.view?.packets && (
                                    <PacketView
                                        title={snapshot.view.title}
                                        packets={snapshot.view.packets}
                                        packetNames={snapshot.view.packetNames}
                                        onPacketNameChange={(index, value) =>
                                            updateStep({
                                                viewPacketNames: {
                                                    ...(step.viewPacketNames ||
                                                        {}),
                                                    [index]: value,
                                                },
                                            })
                                        }
                                        labels={snapshot.view.labels}
                                        pictograms={snapshot.view.pictograms}
                                        layoutRows={snapshot.view.layoutRows}
                                        collectOrder={
                                            snapshot.view.collectOrder
                                        }
                                        collectGroups={
                                            snapshot.view.collectGroups
                                        }
                                        faceDown={afterFaceDown}
                                        warning={snapshot.view.warning}
                                    />
                                )}

                                <PacketView
                                    title="Después"
                                    packets={snapshot.afterPackets}
                                    packetNames={snapshot.afterPacketNames}
                                    onPacketNameChange={(index, value) =>
                                        updateStep({
                                            packetNames: {
                                                ...(step.packetNames || {}),
                                                [index]: value,
                                            },
                                        })
                                    }
                                    faceDown={afterFaceDown}
                                />
                            </Box>
                        </Paper>
                    )}

                    {!model.blocked && (
                        <Paper variant="outlined" sx={{ p: 0.8 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 0.6,
                                }}
                            >
                                Resultado final
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 0.6,
                                }}
                            >
                                {finalFaceDown ? "Dorso ↑" : "Cara ↑"}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.7,
                                }}
                            >
                                {model.simulation?.finalCards.map(
                                    (card, index) => (
                                        <MiniCard
                                            key={`${card.id}-${index}`}
                                            card={card}
                                            position={index + 1}
                                        />
                                    ),
                                )}
                            </Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 0.6,
                                }}
                            >
                                {finalFaceDown ? "Cara ↓" : "Dorso ↓"}
                            </Typography>
                        </Paper>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cerrar</Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        if (selectedPreset) {
                            setIsSavePresetDialogOpen(true);
                            return;
                        }

                        savePreset();
                    }}
                >
                    Guardar receta
                </Button>
            </DialogActions>
            <RadicalSavePresetDialog
                open={isSavePresetDialogOpen}
                onClose={() => setIsSavePresetDialogOpen(false)}
                presetName={selectedPreset?.name}
                title="Guardar receta avanzada"
                newItemText="una receta avanzada nueva"
                onCreate={() => savePreset()}
                onOverwrite={() => savePreset(true)}
            />
            <RadicalConfirmDialog
                open={Boolean(confirmationDialog)}
                onClose={() => setConfirmationDialog("")}
                title={
                    confirmationDialog === "clearCards"
                        ? "Limpiar cartas"
                        : "Borrar receta"
                }
                description={
                    confirmationDialog === "clearCards"
                        ? "Se eliminarán todas las cartas asignadas de la receta avanzada actual. Esta acción no se puede deshacer."
                        : `Se borrará la receta avanzada “${
                              selectedPreset?.name || "seleccionada"
                          }”. Esta acción no se puede deshacer.`
                }
                confirmText={
                    confirmationDialog === "clearCards" ? "Limpiar" : "Borrar"
                }
                onConfirm={() => {
                    if (confirmationDialog === "clearCards") {
                        handleClearCurrentCards();
                    } else {
                        handleDeletePreset();
                    }

                    setConfirmationDialog("");
                }}
            />
            <RadicalUnsavedChangesDialog
                open={pendingPresetId !== null}
                onClose={() => setPendingPresetId(null)}
                currentPresetName={selectedPreset?.name || "la receta actual"}
                nextPresetName={
                    pendingPresetId
                        ? presets.find(
                              (preset) => preset.id === pendingPresetId,
                          )?.name || "otra receta"
                        : "Desde el modo normal"
                }
                onDiscard={() => {
                    const presetId = pendingPresetId;

                    setPendingPresetId(null);
                    loadPresetById(presetId);
                }}
            />
            <RadicalUnsavedChangesDialog
                open={isCloseConfirmationOpen}
                onClose={() => setIsCloseConfirmationOpen(false)}
                message={`Has modificado “${
                    selectedPreset?.name || "la receta actual"
                }”. Al cerrar el modo avanzado se perderán esos cambios.`}
                actionText="Descartar y cerrar"
                onDiscard={() => {
                    setIsCloseConfirmationOpen(false);
                    onClose();
                }}
            />
            <RadicalRenamePresetDialog
                open={isRenamePresetDialogOpen}
                onClose={() => setIsRenamePresetDialogOpen(false)}
                value={presetNameDraft}
                label="Nombre de la receta avanzada"
                onChange={(event) => setPresetNameDraft(event.target.value)}
                onSave={handleRenamePreset}
            />
            <RadicalConfirmDialog
                open={pendingStepIndex !== null}
                onClose={() => setPendingStepIndex(null)}
                title="Borrar paso"
                description={`Se borrará el paso “${
                    pendingStepIndex !== null && sequence[pendingStepIndex]
                        ? STEPS[sequence[pendingStepIndex].type].label(
                              sequence[pendingStepIndex],
                          )
                        : "seleccionado"
                }”. Esta acción no se puede deshacer.`}
                confirmText="Borrar"
                onConfirm={() => {
                    if (pendingStepIndex !== null) {
                        removeStep(pendingStepIndex);
                    }

                    setPendingStepIndex(null);
                }}
            />
            <Dialog
                open={
                    isPictogramDialogOpen &&
                    ["deal", "bevel"].includes(step?.type)
                }
                onClose={() => setIsPictogramDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Pictogramas · {Step?.label(step)}</DialogTitle>

                <DialogContent dividers>
                    {["deal", "bevel"].includes(step?.type) && (
                        <RadicalPictogramSelector
                            packets={Step.radix(step)}
                            pictograms={currentStepPictograms}
                            onPictogramChange={(letter, value) =>
                                updateStep({
                                    pictograms: {
                                        ...(step.pictograms || {}),
                                        [letter]: value,
                                    },
                                })
                            }
                            onLayoutSelect={(layout) =>
                                updateStep({
                                    ...(step.type === "deal"
                                        ? { layoutId: layout.id }
                                        : {}),
                                    pictograms: { ...layout.pictograms },
                                })
                            }
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setIsPictogramDialogOpen(false)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={adding}
                onClose={() => setAdding(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Agregar paso</DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={1.5}>
                        {sequence.length > 0 && (
                            <FormControl size="small" fullWidth>
                                <InputLabel>Posición</InputLabel>

                                <Select
                                    value={newPosition}
                                    label="Posición"
                                    onChange={(event) =>
                                        setNewPosition(event.target.value)
                                    }
                                >
                                    <MenuItem value="before">
                                        Antes del actual
                                    </MenuItem>

                                    <MenuItem value="after">
                                        Después del actual
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        )}

                        <FormControl size="small" fullWidth>
                            <InputLabel>Tipo</InputLabel>

                            <Select
                                value={newType}
                                label="Tipo"
                                onChange={(event) =>
                                    setNewType(event.target.value)
                                }
                            >
                                {STEP_TYPES.map((ItemStep) => (
                                    <MenuItem
                                        key={ItemStep.type}
                                        value={ItemStep.type}
                                        disabled={
                                            ItemStep.type === "bevel" &&
                                            allBevelEdges.includes("long") &&
                                            allBevelEdges.includes("short")
                                        }
                                    >
                                        {ItemStep.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="body2" color="text.secondary">
                            {STEPS[newType]?.description}
                        </Typography>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setAdding(false)}>Cancelar</Button>

                    <Button variant="contained" onClick={addStep}>
                        Agregar
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export const getAdvancedSearchCards = (data = {}) =>
    buildModel(
        (data.sequence || []).filter((step) => STEPS[step?.type]),
        data.values || [],
        data.pictogramsByPackets || {},
    ).assignedCards;

export default RadicalAdvancedMode;
